import { parsePhoneNumberFromString } from 'libphonenumber-js/min'

// Numbers typed without a country code are read in the device's region (en-US -> US).
export const region = (() => {
  try {
    return new Intl.Locale(navigator.language).maximize().region || 'US'
  } catch {
    return 'US'
  }
})()

// E.164 (+15551234567) or null. "Possible" rather than "valid" so a brand-new number range
// isn't rejected by stale metadata.
export function normalize(input) {
  const p = parsePhoneNumberFromString(String(input || ''), region)
  return p && p.isPossible() ? p.number : null
}

export function pretty(e164) {
  const p = parsePhoneNumberFromString(e164)
  if (!p) return e164
  return p.country === region ? p.formatNational() : p.formatInternational()
}

// An Instagram handle from whatever was typed or pasted: "@sam.lee", "sam.lee", or a profile link.
// "" if nothing was typed, false if it can't be a handle (letters, digits, . and _, up to 30).
export function cleanHandle(input) {
  let s = String(input || '').trim()
  if (!s) return ''
  s = s.replace(/^(?:https?:\/\/)?(?:www\.)?(?:instagram\.com|instagr\.am)\//i, '').replace(/[/?#].*$/, '').replace(/^@/, '')
  return /^[A-Za-z0-9._]{1,30}$/.test(s) ? s : false
}

// --- pasted lists ----------------------------------------------------------------------------
// Whatever someone pastes or types: one per line, comma lists, "Sam: 415…", "415… Sam", copied
// Contacts cards ("Sam Lee / mobile / (415) 555-0102"), numbers run together. Returns
// {people: [{name, phone}], unread: [text that looked like a number but wasn't], me: bool}.
// People without a name get name ''. Duplicates are merged; your own number is left out.

const LABELS = /^(?:mobile|cell|cellphone|phone|tel|telephone|iphone|home|work|main|other|whatsapp|signal|text|sms|number|#)$/i
const RUN = /\+?\d[\d \t().\-–]*\d/g
const SEPARATORS = /[\n\r,;•·|]+/

const cleanName = (s) => {
  const name = s
    .replace(/[<>"“”()[\]{}]/g, ' ')
    .replace(/^[\s:=\-–—.*@]+|[\s:=\-–—.*]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  const words = name.split(' ').filter((w) => !LABELS.test(w.replace(/[:.]$/, '')))
  return words.join(' ').slice(0, 60)
}

// A run of digits that may be several numbers ("415 555 0102 415 555 0103"): split at spaces into
// the fewest numbers that all read.
function numbersIn(run) {
  const whole = normalize(run)
  if (whole && run.replace(/\D/g, '').length <= 15) return [whole]
  const groups = run.split(/(?=\+)|\s+/).filter(Boolean)
  const out = []
  let cur = ''
  for (const g of groups) {
    const next = cur ? `${cur} ${g}` : g
    if (cur && (g.startsWith('+') || (normalize(cur) && next.replace(/\D/g, '').length > 11))) {
      const n = normalize(cur)
      if (!n) return []
      out.push(n)
      cur = g
    } else cur = next
  }
  const n = normalize(cur)
  return n ? [...out, n] : []
}

export function parseList(text, myPhone = null) {
  const people = [], unread = []
  let me = false
  let pendingName = ''
  const add = (name, phone) => {
    if (phone === myPhone) return void (me = true)
    const seen = people.find((p) => p.phone === phone)
    if (seen) {
      if (!seen.name && name) seen.name = name
    } else people.push({ name, phone })
  }
  for (const entry of String(text || '').split(SEPARATORS)) {
    const runs = [...entry.matchAll(RUN)].filter((m) => m[0].replace(/\D/g, '').length >= 7)
    if (!runs.length) {
      // A line with just a name, as in a copied contact card: it names the next number.
      const name = cleanName(entry)
      if (/\d{4,}/.test(entry.replace(/\D(?=\d)/g, ''))) unread.push(entry.trim().slice(0, 40))
      else if (name && !/\d/.test(name)) pendingName = name
      continue
    }
    let last = 0
    runs.forEach((m, i) => {
      let nums = numbersIn(m[0])
      let lead = ''
      // "Guest 12 415 555 1011": a short number in the name ran into the phone number. Give leading
      // groups back to the name until the rest reads.
      const groups = m[0].trim().split(/\s+/)
      for (let k = 1; !nums.length && k < groups.length - 1; k++) {
        const rest = groups.slice(k).join(' ')
        if (rest.replace(/\D/g, '').length < 7) break
        nums = numbersIn(rest)
        if (nums.length) lead = groups.slice(0, k).join(' ')
      }
      const before = cleanName(`${entry.slice(last, m.index)} ${lead}`)
      const after = cleanName(entry.slice(m.index + m[0].length, runs[i + 1]?.index ?? entry.length))
      last = m.index + m[0].length
      if (!nums.length) return void unread.push(m[0].trim())
      // "Sam 415…" names it before the number; "415… Sam" after.
      let name = before || (i === runs.length - 1 || !cleanName(entry.slice(last, runs[i + 1].index)) ? after : '') || pendingName
      if (!/\p{L}/u.test(name)) name = '' // digits and punctuation only: a fragment, not a name
      pendingName = ''
      nums.forEach((n, k) => add(k === 0 ? name : '', n))
    })
  }
  return { people, unread, me }
}
